/**
 * AxiomGO brand mark as an inline SVG React component.
 *
 * Renders the canonical engineering/gauge brand symbol extracted from the
 * favicon asset. Always monochromatic — color is controlled entirely by
 * `currentColor` so the parent theme governs appearance (hard rule 6).
 *
 * DO NOT add fill colors, gradients, or teal/green (var(--axg-accent)) here.
 */
import type { SVGProps } from 'react'

interface AxiomLogoProps extends SVGProps<SVGSVGElement> {
  /** Size in px applied to both width and height. Defaults to 20. */
  size?: number
}

/**
 * Inline SVG brand mark for AxiomGO.
 * Scales cleanly to any size; monochromatic via currentColor.
 */
export function AxiomLogo({ size = 20, className, ...props }: AxiomLogoProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={className}
      {...props}
    >
      <g
        transform="translate(16,16)"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      >
        {/* Left arc spine */}
        <path d="M-1,-11 L-1,-9 A9,9 0 0,0 -7.8,4.5 L-9.5,5.5 L-8.5,7.5 L-6.5,6.5 A9,9 0 0,0 -1,9 L-1,11" />
        {/* Full left arc */}
        <path d="M-1,-9 A9,9 0 0,0 -1,9" />
        {/* Top cross-tick */}
        <line x1="-2" y1="-11" x2="1" y2="-11" />
        {/* Bottom-left cross-tick */}
        <line x1="-10.5" y1="5" x2="-9" y2="7" />
        {/* Bottom cross-tick */}
        <line x1="-2" y1="11" x2="1" y2="11" />
        {/* Inner arc */}
        <path d="M-1,-5 A5,5 0 0,0 -1,5" />
        {/* Center horizontal arm */}
        <line x1="2" y1="0" x2="10" y2="0" />
        {/* Upper branch arm */}
        <line x1="2" y1="-4" x2="7" y2="-4" />
        <line x1="7" y1="-4" x2="7" y2="-7" />
        {/* Lower branch arm */}
        <line x1="2" y1="4" x2="7" y2="4" />
        <line x1="7" y1="4" x2="7" y2="7" />
        {/* Terminal dots — filled with currentColor */}
        <circle cx="10" cy="0" r="1" fill="currentColor" stroke="none" />
        <circle cx="7" cy="-7" r="1" fill="currentColor" stroke="none" />
        <circle cx="7" cy="7" r="1" fill="currentColor" stroke="none" />
      </g>
    </svg>
  )
}
